'use client';

import {
  useState,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import dynamic from 'next/dynamic';
import type { FloatingVoiceSurface } from './FloatingVoiceV1';
import './voice-demo-with-cta.css';

const FloatingVoiceV1 = dynamic(
  () => import('@/components/voice/FloatingVoiceV1'),
  { ssr: false },
);

export interface VoiceDemoWithCtaProps {
  /** CTA destination (may already carry UTM params). */
  ctaHref: string;
  /** Appended to `ctaHref` as `utm_content=` when provided. */
  utmContent?: string;
  /** Analytics campaign identifier rendered as `data-campaign`. */
  campaign?: string;
  /** Voice analytics surface — defaults to `homepage`. */
  surface?: FloatingVoiceSurface;
  /** Post-demo heading — "That's Bri." */
  heading?: string;
  /** Post-demo supporting copy. */
  body?: ReactNode;
  /** Label on the conversion CTA. */
  ctaLabel?: string;
  /** Fine print under the CTA. */
  subtext?: string;
  /** Label next to the pulsing online dot. */
  onlineLabel?: string;
  /** Label used by the in-ring post-call CTA. */
  postCallLabel?: string;
  /** Dwell fallback (ms) before the conversion block reveals without interaction. */
  dwellMs?: number;
  /**
   * Reserves vertical space (px) for the lazily-hydrated ring so the page has
   * no layout shift. Opt-in — /x ships without it to keep its visuals as-is.
   */
  reserveMinHeight?: number;
  /** Wrapper class around the ring (e.g. a scoped glow container). */
  glowClassName?: string;
  /** Replaces the default reveal classes when the host page scopes its own CSS. */
  postDemoClassName?: string;
  postDemoVisibleClassName?: string;
  className?: string;
}

function withUtmContent(href: string, utmContent?: string): string {
  if (!utmContent) return href;
  const sep = href.includes('?') ? '&' : '?';
  return `${href}${sep}utm_content=${encodeURIComponent(utmContent)}`;
}

/**
 * Shared "hear Bri → convert" block: FloatingVoiceV1 centered ring plus a
 * post-demo conversion block that slides in after interaction (click / key /
 * focus) or a dwell fallback. Extracted from /x's XWidgetWithCta; /x wraps
 * this with its own scoped class names so its visuals are unchanged.
 */
export function VoiceDemoWithCta({
  ctaHref,
  utmContent,
  campaign,
  surface = 'homepage',
  heading = "That's Bri.",
  body,
  ctaLabel = 'Get started — $99/mo, 30-day guarantee',
  subtext = 'No setup. Forward your number in 60 seconds.',
  onlineLabel = 'Bri is online',
  postCallLabel = 'Get started',
  dwellMs = 5000,
  reserveMinHeight,
  glowClassName,
  postDemoClassName,
  postDemoVisibleClassName,
  className,
}: VoiceDemoWithCtaProps) {
  const [hasInteracted, setHasInteracted] = useState(false);

  // Dwell fallback — reveal the conversion block even without interaction
  useEffect(() => {
    const timer = setTimeout(() => setHasInteracted(true), dwellMs);
    return () => clearTimeout(timer);
  }, [dwellMs]);

  const markInteracted = useCallback(() => setHasInteracted(true), []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') markInteracted();
    },
    [markInteracted],
  );

  const resolvedHref = withUtmContent(ctaHref, utmContent);
  const postClass = postDemoClassName ?? 'vdc-post';
  const postVisibleClass = postDemoVisibleClassName ?? 'vdc-post-visible';

  const bodyContent = body ?? (
    <>
      She&apos;ll answer every call your business misses.
      <br />
      24 hours a day. 7 days a week.
    </>
  );

  return (
    <div
      className={className}
      style={{ maxWidth: 480, margin: '0 auto', padding: '0 24px' }}
    >
      {/* Online indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <span
          className="vdc-dot"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#22c55e',
            display: 'inline-block',
          }}
        />
        <span
          style={{
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 12,
            letterSpacing: '0.04em',
            color: 'var(--dim)',
          }}
        >
          {onlineLabel}
        </span>
      </div>

      {/* Widget (optionally wrapped in a host-provided glow container) */}
      <div
        className={glowClassName}
        data-campaign={campaign}
        onClick={markInteracted}
        onKeyDown={handleKeyDown}
        onFocusCapture={markInteracted}
        role="presentation"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            ...(reserveMinHeight ? { minHeight: reserveMinHeight } : {}),
          }}
        >
          <FloatingVoiceV1
            surface={surface}
            variant="centered-ring"
            postCall={{ mode: 'cta', href: resolvedHref, label: postCallLabel }}
          />
        </div>
      </div>

      {/* Post-demo conversion block — space always reserved, opacity reveal */}
      <div
        className={`${postClass}${hasInteracted ? ` ${postVisibleClass}` : ''}`}
        style={{ marginTop: 32, textAlign: 'center' }}
      >
        <p
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 'clamp(20px, 3vw, 28px)',
            fontWeight: 500,
            color: 'var(--paper)',
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {heading}
        </p>
        <p
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 'clamp(15px, 2vw, 18px)',
            lineHeight: 1.55,
            color: 'var(--dim)',
            marginTop: 12,
            maxWidth: '36ch',
            marginInline: 'auto',
          }}
        >
          {bodyContent}
        </p>

        <div style={{ marginTop: 24 }}>
          <a
            href={resolvedHref}
            style={{
              display: 'block',
              width: '100%',
              padding: '16px 24px',
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              background: 'var(--cyan)',
              borderRadius: 4,
              border: 'none',
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            {ctaLabel}
          </a>
        </div>

        <p
          style={{
            marginTop: 12,
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 12,
            color: 'var(--dim)',
            letterSpacing: '0.02em',
          }}
        >
          {subtext}
        </p>
      </div>
    </div>
  );
}

export default VoiceDemoWithCta;
